import React, { useState, useEffect, useRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  className = '',
  id,
  value,
  defaultValue,
  placeholder,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Determine if label should float
  const currentValue = value !== undefined ? value : defaultValue;
  const shouldFloat = isFocused || hasValue || (currentValue !== undefined && currentValue !== '' && currentValue !== null);

  useEffect(() => {
    // Check initial value
    if (inputRef.current) {
      const hasValueCheck = inputRef.current.value !== '' && inputRef.current.value !== null;
      setHasValue(hasValueCheck);
    }
  }, [currentValue]);

  useEffect(() => {
    // Handle date inputs specially
    if (props.type === 'date' && inputRef.current) {
      setHasValue(inputRef.current.value !== '');
    }
  }, [props.type, currentValue]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setHasValue(e.target.value !== '');
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(e.target.value !== '');
    if (props.onChange) {
      props.onChange(e);
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        {label && (
          <label
            htmlFor={inputId}
            className={`
              absolute left-0 transition-all duration-200 pointer-events-none
              ${shouldFloat
                ? 'top-0 text-xs text-blue-600 font-medium transform -translate-y-1'
                : 'top-3 text-sm text-gray-500'
              }
              ${error ? 'text-red-600' : ''}
            `}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <input
          ref={inputRef}
          id={inputId}
          value={value}
          defaultValue={defaultValue}
          placeholder={shouldFloat ? placeholder : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          className={`
            w-full pt-6 pb-2 px-0 bg-transparent
            border-0 border-b-2 transition-all duration-200
            focus:outline-none focus:ring-0
            ${error 
              ? 'border-red-500 focus:border-red-600' 
              : 'border-gray-300 focus:border-blue-600'
            }
            ${shouldFloat ? 'text-gray-900' : 'text-gray-500'}
            placeholder:text-transparent
            ${className}
          `}
          {...props}
        />
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

